// Typeahead picker over org users — used in PersonReferenceInput when
// the caller wants to link an owner/manager slot to a system user
// (rather than a free-text external contact). Selecting a user emits
// the user's id; the parent then hydrates name + email from the row
// and marks those fields read-only.
//
// All active users regardless of role are pickable (org admins are
// legitimate owner/manager contacts). Disabled accounts are suppressed
// because alerts cannot fire to a disabled account.

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { OrganisationUser } from "../../types";

interface LinkedUserPickerProps {
  /** All org users; the picker applies its own role + status filter. */
  users: OrganisationUser[];
  /** Currently linked user id (if any). */
  value?: string;
  /** Called with the selected user record (or undefined to clear). */
  onSelect: (user: OrganisationUser | undefined) => void;
  /** Disabled when the form is submitting. */
  disabled?: boolean;
  /** aria-label and id-suffix used to scope the picker to its slot. */
  label: string;
}

// Matches the dropdown's max-h-48 (12rem) — used to decide whether to flip
// the portaled list above the input when there's no room below.
const DROPDOWN_MAX_HEIGHT = 192;

// Only disabled accounts are excluded — they cannot receive alerts.
// Role is not a criterion: admins are valid owner/manager contacts and
// the backend already skips ACL grants for users who already hold
// org-wide access.
function isPickable(u: OrganisationUser): boolean {
  if (u.status === "disabled") return false;
  return true;
}

export default function LinkedUserPicker({
  users,
  value,
  onSelect,
  disabled,
  label,
}: LinkedUserPickerProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  // Viewport-relative anchor for the portaled dropdown. Recomputed while
  // open so it tracks scroll/resize of any container.
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!open) {
      // Clears the DOM rect when the dropdown closes; inseparable from the
      // DOM-read / scroll-subscription side of this effect.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAnchorRect(null);
      return;
    }
    const update = () => {
      if (inputRef.current) {
        // Reads getBoundingClientRect() after render — must run in an effect
        // because the layout is not committed until after the render phase.
        setAnchorRect(inputRef.current.getBoundingClientRect());
      }
    };
    update();
    // capture:true so the listener fires for scrolls on any ancestor
    // (the People card's overflow container), not just the window.
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  const selected = value
    ? users.find((u) => u.userId === value)
    : undefined;

  const pickable = useMemo(() => users.filter(isPickable), [users]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pickable;
    return pickable.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q),
    );
  }, [pickable, query]);

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2 bg-slate-50 dark:bg-slate-800">
        <div className="min-w-0">
          <div className="text-body-sm text-slate-900 dark:text-slate-100 truncate">
            {selected.name}
          </div>
          <div className="text-caption text-slate-500 dark:text-slate-400 truncate" title={selected.email}>
            {selected.email}
          </div>
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onSelect(undefined)}
          className="text-caption text-brand-600 hover:text-brand-700 dark:text-brand-400 disabled:opacity-50"
          aria-label={`Clear linked ${label}`}
        >
          Clear
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        className="input"
        placeholder={`Search org users for ${label}`}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay so an option click fires before the dropdown closes.
          setTimeout(() => setOpen(false), 150);
        }}
        disabled={disabled}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listId}
        aria-label={`${label} linked user search`}
      />
      {open &&
        anchorRect &&
        createPortal(
          <ul
            id={listId}
            role="listbox"
            aria-label={`${label} linked user options`}
            style={{
              position: "fixed",
              // Flip above the input when there isn't room below it, so the
              // results never disappear off-screen for a row near the bottom.
              ...(anchorRect.bottom + DROPDOWN_MAX_HEIGHT > window.innerHeight
                ? { bottom: window.innerHeight - anchorRect.top + 4 }
                : { top: anchorRect.bottom + 4 }),
              left: anchorRect.left,
              width: anchorRect.width,
              zIndex: 50,
            }}
            // Rendered in a body portal (not absolutely positioned inside the
            // People card) so the card's overflow-x-auto wrapper can't clip
            // it — the regression where results were trapped in a scroll box.
            className="max-h-48 min-w-[280px] overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-soft-lg dark:shadow-soft-lg-dark"
          >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-body-sm text-slate-500 dark:text-slate-400">
              No users match.
            </li>
          ) : (
            filtered.map((u) => (
              <li
                key={u.userId}
                role="option"
                aria-selected="false"
                aria-label={u.name}
                tabIndex={0}
                onMouseDown={(e) => {
                  // onMouseDown fires before input blur — keeps the click
                  // from racing the dropdown close.
                  e.preventDefault();
                  onSelect(u);
                  setQuery("");
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(u);
                    setQuery("");
                    setOpen(false);
                  }
                }}
                className="px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-hidden focus:bg-slate-50 dark:focus:bg-slate-700"
              >
                <div className="text-body-sm text-slate-900 dark:text-slate-100">
                  {u.name}
                </div>
                <div
                  className="text-caption text-slate-500 dark:text-slate-400 truncate"
                  title={u.email}
                >
                  {u.email}
                </div>
              </li>
            ))
          )}
          </ul>,
          document.body,
        )}
    </div>
  );
}
